/**
 * Eigener Anreise–Abreise-Kalender (Vanilla), angelehnt an frontend/src/components/UzDateRangeInput.vue
 * — kein Flatpickr, keine fertige Kalender-Komponente von Drittanbietern.
 */
import dateRangePickerCss from './date-range-picker.css?inline'
import { addDaysYmd, formatYmdFromDate, todayYmd } from './calendarDate'
import {
  buildConstraintCtx,
  endDisabledReason,
  isEndDisabled,
  isStartDisabled,
  startDisabledReason,
  type WidgetSeasonPeriod,
  type WidgetStayConstraints,
} from './stay-calendar-constraints'
import {
  buildOccupancyRgbMaps,
  occupancyArrivalLayerStyle,
  occupancyDepartureLayerStyle,
  occupancyNightLayerStyle,
  selectionEndHalfStyle,
  selectionStartHalfStyle,
} from './date-range-picker-visual'

export function getDateRangePickerCss(): string {
  return dateRangePickerCss
}

const monthLabels = [
  'Januar',
  'Februar',
  'Maerz',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]
const weekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

function fromIsoLocal(value: string): Date | null {
  const d = new Date(`${value.trim()}T00:00:00`)
  if (Number.isNaN(d.getTime())) return null
  return d
}

function formatDisplay(iso: string): string {
  const d = fromIsoLocal(iso)
  if (!d) return iso
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${d.getFullYear()}`
}

/** Backend: 1 = Mo … 7 = So (wie weekdayNumberFromIsoDate). */
function formatAllowedWeekdayNums(nums: readonly number[]): string {
  const u = [...new Set(nums)]
    .filter((n) => n >= 1 && n <= 7)
    .sort((a, b) => a - b)
  if (u.length === 0) return ''
  return u.map((n) => weekdayLabels[n - 1]).join(', ')
}

function isAllWeekdays(nums: readonly number[]): boolean {
  const u = new Set(nums.filter((n) => n >= 1 && n <= 7))
  return u.size === 7
}

/** Pro Saison wie in der API — keine Vereinigung über alle Zeiträume. */
function appendSeasonWeekdayLegend(container: HTMLElement, periods: WidgetSeasonPeriod[]): void {
  const rows: string[] = []
  for (const p of periods) {
    const arr = p.allowed_arrival_weekdays ?? []
    const dep = p.allowed_departure_weekdays ?? []
    if (arr.length === 0 && dep.length === 0) continue
    const an = isAllWeekdays(arr) ? 'alle Tage' : formatAllowedWeekdayNums(arr) || '—'
    const ab = isAllWeekdays(dep) ? 'alle Tage' : formatAllowedWeekdayNums(dep) || '—'
    const name = (p.season_name ?? '').trim() || 'Saison'
    rows.push(`${name}: An ${an}, Ab ${ab}`)
  }
  if (rows.length === 0) return
  const uniqueRows = [...new Set(rows)]
  const title = document.createElement('div')
  title.className = 'uzw-v2-date-range__legend-block uzw-v2-date-range__legend-block--section-title'
  title.textContent = 'An-/Abreisetage je Saison'
  container.appendChild(title)
  for (const text of uniqueRows) {
    const el = document.createElement('div')
    el.className = 'uzw-v2-date-range__legend-block uzw-v2-date-range__legend-block--season'
    el.textContent = text
    container.appendChild(el)
  }
}

function appendLegendKey(swatchClass: string, label: string): HTMLElement {
  const key = document.createElement('div')
  key.className = 'uzw-v2-date-range__legend-key'
  const sw = document.createElement('span')
  sw.className = `uzw-v2-date-range__legend-swatch ${swatchClass}`
  sw.setAttribute('aria-hidden', 'true')
  const text = document.createElement('span')
  text.className = 'uzw-v2-date-range__legend-key-label'
  text.textContent = label
  key.append(sw, text)
  return key
}

function appendLegendAvailabilityKeys(container: HTMLElement): void {
  const wrap = document.createElement('div')
  wrap.className = 'uzw-v2-date-range__legend-keys'
  wrap.append(
    appendLegendKey('uzw-v2-date-range__legend-swatch--avail', 'Frei'),
    appendLegendKey('uzw-v2-date-range__legend-swatch--occ', 'Belegt'),
  )
  container.appendChild(wrap)
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function buildDayCells(viewMonth: Date): Array<{ iso: string; day: number; inMonth: boolean }> {
  const out: Array<{ iso: string; day: number; inMonth: boolean }> = []
  const start = monthStart(viewMonth)
  const firstWeekday = (start.getDay() + 6) % 7
  const gridStart = new Date(start)
  gridStart.setDate(gridStart.getDate() - firstWeekday)
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart)
    d.setDate(gridStart.getDate() + i)
    out.push({
      iso: formatYmdFromDate(d),
      day: d.getDate(),
      inMonth: d.getMonth() === viewMonth.getMonth(),
    })
  }
  return out
}

export type DateRangePickerOptions = {
  form: HTMLFormElement | null
  /** Nur Detail-Widget: Belegung + Saisonregeln vom Backend */
  constraints?: WidgetStayConstraints | null
}

type OpenController = { close: () => void }

let activeOpen: OpenController | null = null

function mountCustomDateRange(root: HTMLElement, constraints: WidgetStayConstraints | null | undefined): void {
  const fromInput = root.querySelector('input[name="stay_from"]') as HTMLInputElement | null
  const toInput = root.querySelector('input[name="stay_to"]') as HTMLInputElement | null
  const trigger = root.querySelector('[data-uzw-v2-date-range-trigger]') as HTMLButtonElement | null
  if (!fromInput || !toInput || !trigger) return

  const valueEl = trigger.querySelector('[data-uzw-v2-date-range-value]') as HTMLElement | null
  const hintEl = trigger.querySelector('[data-uzw-v2-date-range-hint]') as HTMLElement | null

  let open = false
  let panel: HTMLElement | null = null
  let viewMonth = monthStart(fromIsoLocal(fromInput.value.trim()) ?? new Date())
  let outsideAbort: AbortController | null = null

  const constraintCtx = constraints ? buildConstraintCtx(constraints, todayYmd()) : null

  const readStart = (): string => fromInput.value.trim()
  const readEnd = (): string => toInput.value.trim()
  const selectingEnd = (): boolean => !!readStart() && !readEnd()
  const todayIso = (): string => todayYmd()

  const isDisabledIso = (iso: string): boolean => {
    if (selectingEnd()) {
      const rs = readStart()
      if (constraintCtx) return isEndDisabled(iso, rs, constraintCtx)
      if (iso < todayIso()) return true
      if (rs && iso <= rs) return true
      return false
    }
    if (constraintCtx) return isStartDisabled(iso, constraintCtx)
    return iso < todayIso()
  }

  const disabledReason = (iso: string): string => {
    if (selectingEnd()) {
      return endDisabledReason(iso, readStart(), constraintCtx)
    }
    return startDisabledReason(iso, constraintCtx)
  }

  const isSelectedStart = (iso: string): boolean => !!readStart() && iso === readStart()
  const isSelectedEnd = (iso: string): boolean => !!readEnd() && iso === readEnd()
  const isInRange = (iso: string): boolean => {
    const s = readStart()
    const e = readEnd()
    if (!s || !e) return false
    return iso > s && iso < e
  }

  const selectionHintText = (): string => {
    if (selectingEnd()) return 'Abreise waehlen'
    if (readStart() && readEnd()) return 'Neu waehlen'
    return 'Anreise waehlen'
  }

  const triggerLabelText = (): string => {
    const sf = readStart()
    const st = readEnd()
    if (sf && st) return `${formatDisplay(sf)} – ${formatDisplay(st)}`
    if (sf) return `${formatDisplay(sf)} – …`
    return 'Zeitraum waehlen'
  }

  const syncTrigger = (): void => {
    if (valueEl) valueEl.textContent = triggerLabelText()
    if (hintEl) hintEl.textContent = selectionHintText()
  }

  const dispatchHidden = (): void => {
    fromInput.dispatchEvent(new Event('input', { bubbles: true }))
    toInput.dispatchEvent(new Event('input', { bubbles: true }))
    fromInput.dispatchEvent(new Event('change', { bubbles: true }))
    toInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  const removeOutsideListeners = (): void => {
    outsideAbort?.abort()
    outsideAbort = null
  }

  const closePanel = (): void => {
    open = false
    trigger.setAttribute('aria-expanded', 'false')
    if (panel) {
      panel.remove()
      panel = null
    }
    removeOutsideListeners()
    if (activeOpen?.close === closePanel) activeOpen = null
  }

  const renderPanelContent = (body: HTMLElement): void => {
    body.replaceChildren()
    const head = document.createElement('div')
    head.className = 'uzw-v2-date-range__head'
    const prev = document.createElement('button')
    prev.type = 'button'
    prev.className = 'uzw-v2-date-range__icon-btn'
    prev.setAttribute('aria-label', 'Vorheriger Monat')
    prev.textContent = '‹'
    prev.addEventListener('click', () => {
      const d = new Date(viewMonth)
      d.setMonth(d.getMonth() - 1)
      viewMonth = monthStart(d)
      if (panel) renderPanelContent(panel)
    })
    const title = document.createElement('div')
    title.className = 'uzw-v2-date-range__month'
    title.textContent = `${monthLabels[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`
    const actions = document.createElement('div')
    actions.className = 'uzw-v2-date-range__head-actions'
    const next = document.createElement('button')
    next.type = 'button'
    next.className = 'uzw-v2-date-range__icon-btn'
    next.setAttribute('aria-label', 'Naechster Monat')
    next.textContent = '›'
    next.addEventListener('click', () => {
      const d = new Date(viewMonth)
      d.setMonth(d.getMonth() + 1)
      viewMonth = monthStart(d)
      if (panel) renderPanelContent(panel)
    })
    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.className = 'uzw-v2-date-range__icon-btn'
    closeBtn.setAttribute('aria-label', 'Schliessen')
    closeBtn.textContent = '×'
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      closePanel()
    })
    actions.append(next, closeBtn)
    head.append(prev, title, actions)

    const hintBar = document.createElement('div')
    hintBar.className = 'uzw-v2-date-range__hint-bar'
    hintBar.textContent = selectionHintText()

    const wk = document.createElement('div')
    wk.className = 'uzw-v2-date-range__weekdays'
    for (const w of weekdayLabels) {
      const c = document.createElement('div')
      c.textContent = w
      wk.appendChild(c)
    }

    const occMaps =
      constraintCtx && constraintCtx.blocks.length > 0
        ? buildOccupancyRgbMaps(constraintCtx.blocks)
        : null

    const cells = buildDayCells(viewMonth)
    const sf = readStart()
    const st = readEnd()
    const inRangeIso = (iso: string): boolean =>
      !!(sf && st && iso > sf && iso < st)
    const bridgeLeft = (idx: number, iso: string): boolean => {
      if (!inRangeIso(iso) || idx % 7 === 0) return false
      const prevIso = addDaysYmd(iso, -1)
      if (!prevIso || cells[idx - 1]?.iso !== prevIso) return false
      return prevIso === sf || inRangeIso(prevIso)
    }
    const bridgeRight = (idx: number, iso: string): boolean => {
      if (!inRangeIso(iso) || idx % 7 === 6) return false
      const nextIso = addDaysYmd(iso, 1)
      if (!nextIso || cells[idx + 1]?.iso !== nextIso) return false
      return nextIso === st || inRangeIso(nextIso)
    }

    const grid = document.createElement('div')
    grid.className = 'uzw-v2-date-range__days'
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i]!
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'uzw-v2-date-range__day'
      if (!cell.inMonth) btn.classList.add('uzw-v2-date-range__day--muted')

      const dis = isDisabledIso(cell.iso)
      const startSel = isSelectedStart(cell.iso)
      const endSel = isSelectedEnd(cell.iso)
      const range = isInRange(cell.iso)

      // Fremdbelegung nicht unter gewaehlter An-/Abreise legen (sonst uneinheitliche Mai-Darstellung).
      if (occMaps && !range && !startSel && !endSel) {
        const iso = cell.iso
        if (occMaps.night.has(iso)) {
          const lay = document.createElement('span')
          lay.setAttribute('aria-hidden', 'true')
          lay.style.cssText = `position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:0;${occupancyNightLayerStyle(occMaps.night.get(iso)!)}`
          btn.appendChild(lay)
        }
        if (occMaps.arrival.has(iso)) {
          const lay = document.createElement('span')
          lay.setAttribute('aria-hidden', 'true')
          lay.style.cssText = `position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:1;${occupancyArrivalLayerStyle(occMaps.arrival.get(iso)!)}`
          btn.appendChild(lay)
        }
        if (occMaps.departure.has(iso)) {
          const lay = document.createElement('span')
          lay.setAttribute('aria-hidden', 'true')
          lay.style.cssText = `position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:1;${occupancyDepartureLayerStyle(occMaps.departure.get(iso)!)}`
          btn.appendChild(lay)
        }
      }

      if (range) {
        btn.classList.add('uzw-v2-date-range__day--in-range')
        if (bridgeLeft(i, cell.iso)) btn.classList.add('uzw-v2-date-range__day--in-range-bridge-left')
        if (bridgeRight(i, cell.iso)) btn.classList.add('uzw-v2-date-range__day--in-range-bridge-right')
      }

      if (startSel || endSel) {
        btn.classList.add('uzw-v2-date-range__day--sel-edge')
        if (startSel) {
          const lay = document.createElement('span')
          lay.setAttribute('aria-hidden', 'true')
          lay.style.cssText = `position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:3;${selectionStartHalfStyle()}`
          btn.appendChild(lay)
        }
        if (endSel) {
          const lay = document.createElement('span')
          lay.setAttribute('aria-hidden', 'true')
          lay.style.cssText = `position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:3;${selectionEndHalfStyle()}`
          btn.appendChild(lay)
        }
        if (startSel) {
          const nextIso = addDaysYmd(cell.iso, 1)
          const rightCell = i % 7 < 6 ? cells[i + 1] : null
          if (nextIso && rightCell?.iso === nextIso && inRangeIso(nextIso)) {
            btn.classList.add('uzw-v2-date-range__day--sel-edge-bridge-right')
          }
        }
        if (endSel) {
          const prevIso = addDaysYmd(cell.iso, -1)
          const leftCell = i % 7 > 0 ? cells[i - 1] : null
          if (prevIso && leftCell?.iso === prevIso && inRangeIso(prevIso)) {
            btn.classList.add('uzw-v2-date-range__day--sel-edge-bridge-left')
          }
        }
      }

      const dayNum = document.createElement('span')
      dayNum.className = 'uzw-v2-date-range__day-num'
      if (startSel || endSel) dayNum.classList.add('uzw-v2-date-range__day-num--edge')
      dayNum.textContent = String(cell.day)
      btn.appendChild(dayNum)

      if (dis) {
        btn.disabled = true
        const r = disabledReason(cell.iso)
        if (r) btn.title = r
        else if (cell.iso < todayIso()) btn.title = 'Vergangene Tage sind nicht waehlbar.'
        else if (readStart() && cell.iso <= readStart()) btn.title = 'Abreise muss nach Anreise liegen.'
      }
      btn.addEventListener('click', () => {
        if (dis) return
        if (!selectingEnd()) {
          fromInput.value = cell.iso
          toInput.value = ''
        } else {
          toInput.value = cell.iso
          closePanel()
        }
        syncTrigger()
        dispatchHidden()
        if (open && panel) renderPanelContent(panel)
      })
      grid.appendChild(btn)
    }

    const foot = document.createElement('div')
    foot.className = 'uzw-v2-date-range__footer'
    const reset = document.createElement('button')
    reset.type = 'button'
    reset.className = 'uzw-v2-date-range__reset'
    reset.textContent = 'Zuruecksetzen'
    reset.addEventListener('click', () => {
      fromInput.value = ''
      toInput.value = ''
      syncTrigger()
      dispatchHidden()
      if (open && panel) renderPanelContent(panel)
    })
    foot.appendChild(reset)

    body.append(head, hintBar, wk, grid)

    const leg = document.createElement('div')
    leg.className = 'uzw-v2-date-range__legend'
    const hasOccupancyData = Boolean(constraintCtx && constraintCtx.blocks.length > 0)
    if (hasOccupancyData) appendLegendAvailabilityKeys(leg)

    if (sf) {
      const sel = document.createElement('div')
      sel.className = 'uzw-v2-date-range__legend-block uzw-v2-date-range__legend-block--selection'
      sel.textContent = st
        ? `Ihre Anreise (${formatDisplay(sf)}) / Abreise (${formatDisplay(st)})`
        : `Ihre Anreise (${formatDisplay(sf)}) — Abreise noch offen`
      leg.appendChild(sel)
    }

    if (constraintCtx && constraintCtx.periods.length > 0) {
      appendSeasonWeekdayLegend(leg, constraintCtx.periods)
    }

    if (leg.childElementCount > 0) body.appendChild(leg)

    body.appendChild(foot)
  }

  const openPanel = (): void => {
    if (activeOpen && activeOpen.close !== closePanel) activeOpen.close()
    viewMonth = monthStart(fromIsoLocal(readStart()) ?? new Date())
    open = true
    trigger.setAttribute('aria-expanded', 'true')
    panel = document.createElement('div')
    panel.className = 'uzw-v2-date-range__panel'
    panel.setAttribute('role', 'dialog')
    panel.setAttribute('aria-label', 'Reisezeitraum')
    panel.addEventListener('mousedown', (e) => e.stopPropagation())
    renderPanelContent(panel)
    root.appendChild(panel)
    activeOpen = { close: closePanel }

    outsideAbort = new AbortController()
    const { signal } = outsideAbort
    document.addEventListener(
      'mousedown',
      (ev: MouseEvent) => {
        if (!(ev.target instanceof Node)) return
        if (!root.contains(ev.target)) closePanel()
      },
      { capture: true, signal },
    )
    document.addEventListener(
      'keydown',
      (ev: KeyboardEvent) => {
        if (ev.key === 'Escape') closePanel()
      },
      { signal },
    )
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation()
    if (open) closePanel()
    else openPanel()
  })

  syncTrigger()
}

/** Findet alle `[data-uzw-v2-date-range]`-Wurzeln im Formular und bindet den Kalender. */
export function enhanceDateRangePicker(opts: DateRangePickerOptions): void {
  const form = opts.form
  if (!form) return
  const constraints = opts.constraints ?? null
  for (const el of form.querySelectorAll('[data-uzw-v2-date-range]')) {
    if (!(el instanceof HTMLElement)) continue
    if (el.dataset.uzwV2DateRangeReady === '1') continue
    el.dataset.uzwV2DateRangeReady = '1'
    mountCustomDateRange(el, constraints)
  }
}
